<template>
  <div class="screen optin">
    <div class="optin__sheet" :class="{ 'optin__sheet--form': isForm }">
      <!-- inline back link (Figma 443:271 — msisdn / verify) -->
      <button v-if="showPrev" class="optin__prev" type="button" @click="prevSub">
        <svg width="24" height="17" viewBox="0 0 20 14" fill="none" aria-hidden="true">
          <path
            d="M19 7H2M8 1L2 7l6 6"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        Vorige stap
      </button>

      <div class="optin__mid">
        <!-- §13.1 communicatie nieuwe ronde -->
        <template v-if="current === 'communication'">
          <template v-if="sub === 'choice'">
            <div class="bubble" aria-hidden="true">
              <img :src="bubbleImg" class="bubble__shape" alt="" />
              <img :src="trophyImg" class="bubble__art bubble__art--trophy" alt="" />
            </div>
            <h1 class="optin__q">Wil je als eerste weten of je prijs hebt?</h1>
            <button class="optin__primary optin__primary--inline" @click="sub = 'msisdn'">
              Ik wil op de hoogte zijn
            </button>
          </template>

          <template v-else-if="sub === 'msisdn'">
            <h2 class="optin__heading">Dat is goed!</h2>
            <p class="optin__sub">Op welk mobiele nummer wil je de prijsuitrijking ontvangen?</p>
            <label class="field">
              <span>Mobiel nummer</span>
              <input v-model="msisdn" type="tel" inputmode="tel" placeholder="06 1234 5678" />
            </label>
            <p v-if="msisdnError" class="optin__error" role="alert">{{ msisdnError }}</p>
          </template>

          <template v-else>
            <h2 class="optin__heading">Bevestig je mobiele nummer</h2>
            <p class="optin__sub">
              Vul de verificatiecode in die je op {{ normalizedMsisdn || 'je nummer' }} hebt
              ontvangen.
            </p>
            <label class="field">
              <span>Verificatienummer</span>
              <input v-model="code" type="text" inputmode="numeric" placeholder="12345" />
            </label>
            <p v-if="codeError" class="optin__error" role="alert">{{ codeError }}</p>
            <button v-if="newCodeNeeded" class="optin__link" type="button" @click="requestNewCode">
              Nieuwe code aanvragen
            </button>
          </template>
        </template>

        <!-- §13.2 wekelijkse reminder -->
        <template v-else-if="current === 'reminder'">
          <div class="bubble" aria-hidden="true">
            <img :src="bubbleImg" class="bubble__shape" alt="" />
            <img :src="bellImg" class="bubble__art bubble__art--bell" alt="" />
          </div>
          <h1 class="optin__q">Wil je elke week een bericht als je weer kunt spelen?</h1>
          <p class="optin__lead">
            Elke week kun je opnieuw spelen. Hoe meer sterren je verzamelt, hoe meer kans je maakt
            op een prijs.
          </p>
        </template>

        <!-- §13.3 e-mailvalidatie -->
        <template v-else-if="current === 'email'">
          <h2 class="optin__heading">Bevestig je e-mailadres</h2>
          <p class="optin__sub">
            Controleer of dit het e-mailadres is waar je je prijs op wilt ontvangen als je wint.
          </p>
          <label class="field">
            <span>E-mailadres</span>
            <input v-model="email" type="email" inputmode="email" />
          </label>
          <p v-if="emailError" class="optin__error" role="alert">{{ emailError }}</p>
        </template>
      </div>

      <!-- bottom actions -->
      <div class="optin__foot">
        <button
          v-if="current === 'communication' && sub === 'choice'"
          class="optin__link"
          @click="skip"
        >
          Misschien later
        </button>
        <button
          v-else-if="current === 'communication' && sub === 'msisdn'"
          class="optin__primary"
          :disabled="!validMsisdn || busy"
          @click="sendCode"
        >
          Doorgaan
        </button>
        <button
          v-else-if="current === 'communication' && sub === 'verify'"
          class="optin__primary"
          :disabled="busy || newCodeNeeded"
          @click="verify"
        >
          Bevestig
        </button>
        <div v-else-if="current === 'reminder'" class="optin__pair">
          <button class="optin__ghost" :disabled="busy" @click="reminder(false)">
            Liever niet
          </button>
          <button class="optin__primary" :disabled="busy" @click="reminder(true)">Ja, graag</button>
        </div>
        <button
          v-else-if="current === 'email'"
          class="optin__primary"
          :disabled="!validEmail || busy"
          @click="confirmEmail"
        >
          Bevestig
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useGameStore } from '@/stores/game'
import { optinErrorMessage, needsNewCode } from '@/services/optinMessages'
// Figma 421:1749 (the speech bubble) + its 3D art: 421:1752 trophy, 421:1891 bell
import bubbleImg from '@/assets/v3/optin-bubble.svg'
import trophyImg from '@/assets/v3/optin-trophy.webp'
import bellImg from '@/assets/v3/optin-bell.webp'

const store = useGameStore()
const router = useRouter()

const steps = ref([])
const idx = ref(0)
const sub = ref('choice')
const busy = ref(false)

const msisdn = ref('')
const code = ref('')
const email = ref('')
// Error copy per step (see services/optinMessages.js); null = no error shown.
const msisdnError = ref(null)
const codeError = ref(null)
const emailError = ref(null)
// The current code is gone (too many attempts / expired): only a new code helps.
const newCodeNeeded = ref(false)

const current = computed(() => steps.value[idx.value])
const validEmail = computed(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value))

// Houd het invoerveld op alleen cijfers: elk ander teken (spatie, +, streepje,
// letter) wordt al tijdens het typen weggehaald.
watch(msisdn, (v) => {
  const digits = v.replace(/\D/g, '')
  if (digits !== v) msisdn.value = digits
})
// NL mobiel: 06 + 8 cijfers. Een landcode 0031 / 31 (de + is er al af) telt ook
// mee en wordt teruggevouwen naar het nationale 06XXXXXXXX-formaat.
const normalizedMsisdn = computed(() =>
  msisdn.value.replace(/\D/g, '').replace(/^(?:0031|31)/, '0'),
)
const validMsisdn = computed(() => /^06\d{8}$/.test(normalizedMsisdn.value))

// Left-aligned form steps (Figma 421:1763/1808/1902) vs centred bubble steps.
const isForm = computed(
  () =>
    (current.value === 'communication' && (sub.value === 'msisdn' || sub.value === 'verify')) ||
    current.value === 'email',
)
// Inline "Vorige stap" link at the top (Figma 443:271 — msisdn / verify only).
const showPrev = computed(
  () => current.value === 'communication' && (sub.value === 'msisdn' || sub.value === 'verify'),
)

onMounted(() => {
  steps.value = [...store.pendingOptinSteps]
  email.value = store.player?.email || ''
  if (!steps.value.length) router.replace({ name: 'eind' })
})

function advance() {
  sub.value = 'choice'
  if (idx.value < steps.value.length - 1) idx.value += 1
  else router.replace({ name: 'eind' })
}
function skip() {
  advance() // §13.1 "Misschien later" — leaves opt-in unset (asked again next cycle)
}
function prevSub() {
  if (sub.value === 'verify') requestNewCode()
  else if (sub.value === 'msisdn') sub.value = 'choice'
}
function requestNewCode() {
  codeError.value = null
  newCodeNeeded.value = false
  sub.value = 'msisdn'
}

/** Runs a store call; a transport failure becomes a result the error copy understands. */
async function attempt(call) {
  busy.value = true
  try {
    return await call()
  } catch {
    return { ok: false, error: 'network_error' }
  } finally {
    busy.value = false
  }
}

async function sendCode() {
  msisdnError.value = null
  const res = await attempt(() => store.requestMsisdnCode(normalizedMsisdn.value))
  if (!res?.ok) {
    msisdnError.value = optinErrorMessage(res, 'msisdn')
    return // stay on the number step: no code was sent
  }
  code.value = ''
  codeError.value = null
  newCodeNeeded.value = false
  sub.value = 'verify'
}
async function verify() {
  const res = await attempt(() => store.verifyMsisdn(code.value))
  if (res?.ok) {
    await store.setCommunicationOptin(true)
    advance()
  } else {
    codeError.value = optinErrorMessage(res) ?? 'Code klopt niet. Probeer opnieuw.'
    newCodeNeeded.value = needsNewCode(res)
  }
}
async function reminder(accept) {
  busy.value = true
  await store.setReminderOptin(accept)
  busy.value = false
  advance()
}
async function confirmEmail() {
  emailError.value = null
  const res = await attempt(() => store.validateEmail(email.value))
  if (res?.error) {
    emailError.value = optinErrorMessage(res, 'email')
    // A frozen winner keeps the address on file: show it again instead of the typed one.
    if (res.error === 'contact_locked') email.value = store.player?.email || email.value
    return
  }
  advance()
}
</script>

<style lang="scss" scoped>
.optin {
  background: var(--grad-brand);
}
.optin__sheet {
  position: relative;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  // Figma puts the field/CTA edge at 35 and the copy 7px inside that (x=42)
  padding: env(safe-area-inset-top) 35px 56px;
  background: #f1f1f1; // Figma 443:590 — grey sheet
}

// Figma 443:271 — arrow + "Vorige stap", 18px, top-left under the header
.optin__prev {
  position: absolute;
  top: max(env(safe-area-inset-top), var(--sp-4));
  left: 38px; // Figma 443:271
  display: inline-flex;
  align-items: center;
  gap: var(--sp-2);
  font-family: var(--font-brand);
  font-size: var(--fs-lg);
  font-weight: var(--fw-regular);
  color: var(--c-ink);
  background: none;
  border: none;
}

// Centred middle block (bubble steps); vertically balanced for forms too.
.optin__mid {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px; // Figma 421:1886 → 421:1887
  text-align: center;
}
.optin__sheet--form .optin__mid {
  align-items: stretch;
  gap: 8px; // Figma 421:1787 → 421:1791
  text-align: left;
}

// The design's own white speech bubble (Figma 421:1749 / 421:1888 — one Union
// path with the tail cut into its left edge, plus a 0/4px hard shadow) carrying
// the 3D trophy / bell. Everything below is the Figma geometry 1:1: the shape is
// 145.028 wide and 115 tall (the SVG is 119 because the shadow is baked in), and
// each piece of art is placed by its own offset inside it.
.bubble {
  position: relative;
  flex: none;
  width: 145.028px;
  height: 119px;
  // Figma centres the bubble BODY, not the box: with the tail sticking out to
  // the left, its box sits at x=107 on the 390 artboard. The right margin puts
  // it there, and 38 + the 10px stack gap is Figma's 48 to the question.
  margin-right: 31px;
  margin-bottom: 38px;
}
.bubble__shape {
  display: block;
  width: 100%;
  height: 100%;
}
.bubble__art {
  position: absolute;
  // Figma draws both with the same hard drop shadow as the uitleg card art
  filter: drop-shadow(0 4px 0 rgba(0, 0, 0, 0.25));
}
// Figma 421:1752 — 57×73 at (57.03, 21) inside the bubble
.bubble__art--trophy {
  left: 57.03px;
  top: 21px;
  width: 57px;
  height: 73px;
  object-fit: contain;
}
// Figma 421:1891 — 70×77 at (50, 20) inside the bubble
.bubble__art--bell {
  left: 50px;
  top: 20px;
  width: 70px;
  height: 77px;
  object-fit: contain;
}

.optin__q {
  max-width: 300px;
  font-size: var(--fs-2xl); // Figma 23px, centred
  font-weight: var(--fw-bold);
  color: var(--c-ink);
}
.optin__lead {
  max-width: 300px;
  font-size: var(--fs-base);
  font-weight: var(--fw-regular);
  line-height: 1.4;
  color: var(--c-ink-soft);
}
.optin__heading {
  margin-left: 7px; // Figma 421:1787 — copy sits 7px inside the field edge
  font-size: var(--fs-2xl); // Figma 23px bold, left
  font-weight: var(--fw-bold);
  color: var(--c-ink);
}
.optin__sub {
  max-width: 311px; // Figma 421:1791
  margin-left: 7px;
  font-size: var(--fs-base); // Figma 16px light, 62% opacity
  font-weight: var(--fw-regular);
  line-height: 25px; // Figma — 311×50 over two lines
  color: rgba(11, 11, 11, 0.62);
}
.optin__error {
  font-weight: var(--fw-bold);
  color: var(--c-error);
}

.field {
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
  width: 100%;
  margin-top: var(--sp-3);
  text-align: left;
  font-size: var(--fs-base);
  font-weight: var(--fw-bold); // label
  color: var(--c-ink);

  > span {
    margin-left: 7px; // Figma 421:1792 — label at x=42, field at x=35
  }

  input {
    height: 55px; // Figma 421:1796 — pill field
    padding: 0 21px;
    font-size: var(--fs-base);
    font-weight: var(--fw-regular);
    color: var(--c-ink);
    background: var(--c-white);
    border: 1px solid var(--c-field-border);
    border-radius: var(--r-pill);
  }
  input::placeholder {
    color: rgba(11, 11, 11, 0.5);
  }
}

// bottom actions
.optin__foot {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--sp-3);
}
.optin__primary {
  width: 100%;
  max-width: 318px;
  height: 60px; // Figma 318×60, radius 18, 21px
  font-family: var(--font-brand);
  font-size: var(--fs-xl);
  font-weight: var(--fw-bold);
  color: var(--c-white);
  background: var(--c-blue-deep);
  border: none;
  border-radius: var(--r-lg);
  &:disabled {
    opacity: 0.55;
  }
}
.optin__primary--inline {
  // ask step — the CTA sits with the centred block (Figma 421:1745, 25 below)
  margin-top: 15px;
}
.optin__pair {
  display: flex;
  gap: var(--sp-3);
  width: 100%;

  .optin__primary {
    flex: 1 1 0;
    max-width: none;
  }
}
// Figma 421:1882 — "Liever niet" outline button
.optin__ghost {
  flex: 1 1 0;
  height: 60px;
  font-family: var(--font-brand);
  font-size: var(--fs-xl);
  font-weight: var(--fw-regular);
  color: var(--c-ink);
  background: none;
  border: 1px solid #b7b7b7;
  border-radius: var(--r-lg);
  &:disabled {
    opacity: 0.55;
  }
}
// Figma 421:1744 — "Misschien later", 16px light underline
.optin__link {
  font-family: var(--font-brand);
  font-size: var(--fs-base);
  font-weight: var(--fw-regular);
  color: var(--c-ink);
  background: none;
  border: none;
  text-decoration: underline;
}
</style>
